import { useCallback, useRef } from 'react'

import * as Sentry from '@sentry/nextjs'

import { useMarkFtuCompletedMutation } from '@/graphql/generated/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { selectUserId } from '@/redux/me/meSlice'
import {
  addCompletedFtuId,
  mergeCompletedFtuIds,
  selectCompletedFtuIds,
} from '@/redux/ui/uiSlice'

import type { FtuCompletionAdapter } from '../types/types'

/**
 * FTU completion persistence.
 *
 * Redux (persisted to localStorage) is the read source. Marking a tour complete
 * writes Redux first, so the tour disappears at once and stays hidden on this
 * browser even if the backend write fails, then records the completion against
 * the user on the backend so it holds across devices and browsers. The backend's
 * answer is merged back in case another device completed something meanwhile.
 * Hydration from the backend on login lives in useFtuCompletionSync.
 */
export const useFtuCompletion = (): FtuCompletionAdapter => {
  const dispatch = useAppDispatch()
  const completedIds = useAppSelector(selectCompletedFtuIds)
  const userId = useAppSelector(selectUserId)
  const userIdRef = useRef(userId)
  userIdRef.current = userId
  const [markFtuCompletedMutation] = useMarkFtuCompletedMutation()

  const isCompleted = useCallback(
    (id: string) => completedIds.includes(id),
    [completedIds]
  )

  const markCompleted = useCallback(
    (id: string) => {
      const startedByUserId = userIdRef.current
      dispatch(addCompletedFtuId(id))

      markFtuCompletedMutation({ variables: { input: { ftuId: id } } })
        .then(({ data }) => {
          // A response landing after logout, or after another user signed in
          // on this browser, must not repopulate the list resetUi just cleared.
          if (userIdRef.current !== startedByUserId) {
            return
          }
          const backendIds = data?.markFtuCompleted?.userSetting?.completedFtus
          if (backendIds) {
            dispatch(mergeCompletedFtuIds(backendIds))
          }
        })
        .catch((error) => {
          Sentry.captureException(error, {
            tags: { feature: 'first_time_use', action: 'mark_completed' },
            extra: { ftuId: id },
          })
        })
    },
    [dispatch, markFtuCompletedMutation]
  )

  return { completedIds, isCompleted, markCompleted }
}
